import { ReactNode, useEffect, useState } from "react";
import { ChevronLeft, LogOut, Settings, HelpCircle, Handshake, Sparkles } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useSoloModeExperienceState } from "@/hooks/useSoloModeExperienceState";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useLeaderLearningInbox } from "@/hooks/useLeaderLearning";
import { leaderNavTargetActive, myCoachDestination } from '@/lib/coachSessionFollowThrough';
import { useUnreadMessageCount } from "@/hooks/useCoachMessages";
import { CoachingPausedBanner } from "@/components/layout/CoachingPausedBanner";
import { CompletedAccessBanner } from "@/components/layout/CompletedAccessBanner";
import { ArchivedAccessBanner } from "@/components/layout/ArchivedAccessBanner";
import { LeaderGracePeriodBanner } from "@/components/layout/LeaderGracePeriodBanner";
import { LeaderPreviewBanner } from "@/components/layout/LeaderPreviewBanner";
import { ProductFeedbackToolbarActions } from "@/components/feedback/ProductFeedbackToolbarActions";
import { supabase } from "@/integrations/supabase/client";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { getAvatarInitials } from "@/utils/avatar";
import { useLeaderPreview } from "@/preview/leaderPreviewContext";
import { useInvitationContext } from "@/hooks/useInvitationContext";
import { TrialExperience } from "@/components/trial/TrialExperience";
import {
  buildLeaderNavSections,
  resolveLeaderBrandLabel,
  resolveLeaderCoachBridgeVisibility,
} from "@/lib/leaderNavigation";
import {
  LEADER_VEERA_PANEL_ID,
  LEADER_VEERA_PANEL_WIDTH_STYLE,
  useLeaderVeeraPanel,
} from "@/hooks/useLeaderVeeraPanel";

const LEADER_VEERA_PANEL_WIDTH_CLASS = "md:pr-[var(--leader-veera-panel-width)]";
function getLeaderTourId(url: string) {
  if (url === "/today") return "leader-tour-today";
  if (url === "/moments") return "leader-tour-moments";
  if (url === "/insights") return "leader-tour-insights";
  if (url === "/veera") return "leader-tour-veera";
  if (url === "/shared-memory") return "leader-tour-throughline";
  return undefined;
}

const leaderMobileSettingsItem = {
  title: "Settings",
  url: "/settings",
  icon: Settings,
};

function getLeaderMobileTitle(title: string, url: string) {
  if (url === "/moments") return "Schedule";
  if (url === "/shared-memory") return "Leadership Insights";
  if (url === "/veera") return "Coach Veera";
  return title;
}
interface LeaderLayoutProps {
  children: ReactNode;
  suppressLifecycleAccessBanners?: boolean;
  pageContext?: {
    pageName: string;
    data?: Record<string, unknown>;
  };
}

export type LeaderProfileIdentity = {
  userId: string;
  name: string | null;
};

export function resolveActiveLeaderProfileName(
  profileIdentity: LeaderProfileIdentity | null,
  userId?: string | null,
) {
  return profileIdentity?.userId === userId ? profileIdentity.name : null;
}

export function resolveLeaderSidebarIdentity({
  previewEnabled,
  previewName,
  previewInitials,
  profileName,
  metadataName,
  email,
}: {
  previewEnabled: boolean;
  previewName: string;
  previewInitials: string;
  profileName?: string | null;
  metadataName?: unknown;
  email?: string | null;
}) {
  if (previewEnabled) {
    return {
      label: previewName,
      initials: previewInitials,
    };
  }

  const resolvedName = profileName?.trim() || (typeof metadataName === "string" ? metadataName.trim() : "") || "";
  const label = resolvedName || email || "Leader";
  return {
    label,
    initials: getAvatarInitials(resolvedName, email),
  };
}

export function LeaderLayout({
  children,
  suppressLifecycleAccessBanners = false,
  pageContext,
}: LeaderLayoutProps) {
  const preview = useLeaderPreview();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [profileIdentity, setProfileIdentity] = useState<LeaderProfileIdentity | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    signOut
  } = useAuth();
  const { data: invitationContext, isLoading: invitationContextLoading } = useInvitationContext();
  const {data:unreadMessages}=useUnreadMessageCount(user?.id,invitationContext?.coachId??undefined,{requireCoachScope:true});
  const learningInbox = useLeaderLearningInbox(user?.id, invitationContext?.coachId ?? undefined);
  const newLearningCount=learningInbox.count;
  const soloModeExperience = useSoloModeExperienceState();
  const leaderVeeraPanel = useLeaderVeeraPanel();
  const hasCoachConnection =
    preview.enabled ||
    invitationContextLoading ||
    Boolean(invitationContext?.coachId) ||
    soloModeExperience.hasCoachConnection;
  const showCoachBridge = resolveLeaderCoachBridgeVisibility({
    hasCoachConnection,
    invitationContextLoading,
    accountModeLoading: soloModeExperience.isLoading,
    accountMode: soloModeExperience.accountMode,
    previewEnabled: preview.enabled,
  });
  const showLeadershipFrameworkLink =
    !preview.enabled &&
    !hasCoachConnection &&
    !invitationContextLoading &&
    !soloModeExperience.isLoading &&
    (soloModeExperience.accountMode === "solo_trial" || soloModeExperience.accountMode === null);
  const leaderNavSections = buildLeaderNavSections({
    hasCoachConnection,
    includeSettings: !preview.enabled,
    showLeadershipFrameworkLink,
  });
  const brandLabel = resolveLeaderBrandLabel(hasCoachConnection);
  const profileName = resolveActiveLeaderProfileName(profileIdentity, user?.id);
  const { label: userLabel, initials: userInitials } = resolveLeaderSidebarIdentity({
    previewEnabled: preview.enabled,
    previewName: preview.leaderName,
    previewInitials: preview.leaderInitials,
    profileName,
    metadataName: user?.user_metadata?.full_name,
    email: user?.email,
  });
  useEffect(() => {
    if (import.meta.env.MODE === "test") return;
    if (preview.enabled || !user?.id) {
      setProfileIdentity(null);
      return;
    }

    let isActive = true;
    const userId = user.id;
    setProfileIdentity(null);

    const updateSidebarRollout = async () => {
      const {
        data,
        error
      } = (await supabase.from("profiles").select("full_name, sidebar_reorganized_date").eq("id", userId).maybeSingle()) as {
        data: {
          full_name?: string | null;
          sidebar_reorganized_date?: string;
        } | null;
        error: unknown;
      };
      if (error) {
        console.error("Failed to read sidebar rollout date:", error);
        if (isActive) {
          setProfileIdentity({ userId, name: null });
        }
        return;
      }
      if (!isActive) return;

      setProfileIdentity({ userId, name: data?.full_name?.trim() || null });
      if (!data?.sidebar_reorganized_date) {
        const updatePayload = {
          sidebar_reorganized_date: new Date().toISOString()
        };
        const {
          error: updateError
        } = await supabase.from("profiles").update(updatePayload).eq("id", userId);
        if (updateError) {
          console.error("Failed to update sidebar rollout date:", updateError);
        }
      }
    };
    updateSidebarRollout();

    return () => {
      isActive = false;
    };
  }, [preview.enabled, user?.id]);

  const getBadgeCount = (badgeType?: string) => {
    if (badgeType === "messages") return (unreadMessages || 0) + newLearningCount;
    return 0;
  };

  const getBadgeStyle = () => {
    return "bg-primary text-primary-foreground";
  };

  const mapTargetUrl = (url: string) => {
    if (preview.enabled) {
      return preview.mapLeaderPathToPreview(url);
    }

    return url==='/leader/coach'?myCoachDestination({messages:unreadMessages??0,feedback:newLearningCount,publicationId:learningInbox.publicationId,developmentPublicationId:learningInbox.developmentPublicationId}):url;
  };

  const navigateToLeaderPath = (path: string) => {
    navigate(preview.enabled ? preview.mapLeaderPathToPreview(path) : path);
  };
  const leaderMobileNavItems = [
    ...leaderNavSections.flatMap(section => section.items).filter(item =>
      item.mobile !== false && (preview.enabled || item.url !== "/veera")
    ),
    leaderMobileSettingsItem,
  ];
  const livePanelOpen = !preview.enabled && leaderVeeraPanel.open;
  const effectiveCollapsed = collapsed || livePanelOpen;
  const pathnamePageName = location.pathname.split("/").filter(Boolean)[0] || "leader";
  const currentLeaderPage = pageContext?.pageName.trim() || pathnamePageName;

  // Mobile layout with bottom nav
  if (isMobile) {
    return <div
        data-testid="leader-layout-page"
        aria-hidden={livePanelOpen || undefined}
        {...(livePanelOpen ? { inert: "" } : {})}
        className={cn(
          "min-h-[100dvh] bg-background flex flex-col transition-opacity [transition-duration:250ms] ease-out motion-reduce:transition-none",
          livePanelOpen && "pointer-events-none opacity-0",
        )}
      >
        {preview.enabled && <LeaderPreviewBanner />}
        {!suppressLifecycleAccessBanners ? (
          <>
            <CoachingPausedBanner />
            <CompletedAccessBanner />
            <ArchivedAccessBanner />
            <LeaderGracePeriodBanner />
          </>
        ) : null}
        <div
          data-testid="leader-mobile-header"
          className="flex min-w-0 flex-nowrap items-center justify-between gap-2 px-4 py-3 border-b border-border bg-background"
        >
          <span
            data-testid="leader-mobile-brand"
            className="min-w-0 flex-1 truncate text-base font-semibold text-foreground"
          >
            {brandLabel}
          </span>
          <div data-testid="leader-mobile-toolbar-actions" className="flex shrink-0 flex-nowrap items-center gap-2">
            {!preview.enabled ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Coach Veera"
                aria-controls={LEADER_VEERA_PANEL_ID}
                aria-expanded={leaderVeeraPanel.open}
                onClick={(event) => leaderVeeraPanel.openPanel(
                  { pageName: currentLeaderPage, source: "leader_mobile_header" },
                  event.currentTarget,
                )}
                className="h-8 w-8 shrink-0 border-violet-200 bg-violet-50 text-violet-950 hover:bg-violet-100"
              >
                <Sparkles className="h-4 w-4" />
              </Button>
            ) : null}
            {showCoachBridge && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Find a Coach"
                title="Find a Coach"
                className="h-8 w-8 shrink-0 min-[390px]:w-auto min-[390px]:gap-1.5 min-[390px]:px-3"
                onClick={() => navigateToLeaderPath("/leader/talk-with-coach")}
              >
                <Handshake className="h-4 w-4" />
                <span className="hidden min-[390px]:inline">Find a Coach</span>
              </Button>
            )}
            <ProductFeedbackToolbarActions
              reporterRole="leader"
              iconOnly
              className="shrink-0 flex-nowrap gap-1"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center rounded-full border border-border p-1">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                {showCoachBridge && <DropdownMenuItem onClick={() => navigateToLeaderPath("/leader/talk-with-coach")}>
                  <Handshake className="mr-2 h-4 w-4" />
                  Find a Coach
                </DropdownMenuItem>}
                <DropdownMenuItem onClick={() => navigateToLeaderPath("/help")}>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Help & Support
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => preview.enabled ? window.close() : void signOut()} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {preview.enabled ? "Close preview" : "Sign out"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <TrialExperience role="leader" compact mobileRow isPreview={preview.enabled} />
        <main className="flex-1 pb-20">
          {children}
        </main>
        
        {/* Mobile Bottom Nav */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-md safe-bottom">
          <div
            data-testid="leader-mobile-nav-row"
            className="mx-auto grid h-18 max-w-lg items-stretch gap-1 px-2 py-1"
            style={{ gridTemplateColumns: `repeat(${leaderMobileNavItems.length}, minmax(0, 1fr))` }}
          >
            {leaderMobileNavItems.map(item => {
            const targetUrl = mapTargetUrl(item.url);
            const isActive = leaderNavTargetActive(location.pathname,location.hash,targetUrl);
            const badgeCount = getBadgeCount(item.badge);
            const badgeStyle = getBadgeStyle();
            const mobileTitle = getLeaderMobileTitle(item.title, item.url);
            return <NavLink key={item.url} to={targetUrl} data-tour-id={getLeaderTourId(item.url)} className={cn("relative flex h-full min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-center transition-all duration-200", isActive ? "text-primary" : "text-muted-foreground hover:text-foreground")}>
                  <div className="relative flex h-5 items-center justify-center">
                    <item.icon className={cn("h-5 w-5 transition-transform duration-200", isActive && "scale-110")} />
                    {badgeCount > 0 && <span className={cn("absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold", badgeStyle)}>
                        {badgeCount > 9 ? "9+" : badgeCount}
                      </span>}
                  </div>
                  <span className={cn("text-[10px] leading-tight", isActive ? "font-medium" : "font-normal")}>
                    {mobileTitle}
                  </span>
                </NavLink>;
          })}
          </div>
        </nav>
      </div>;
  }

  // Desktop layout with sidebar
  return <div className="min-h-screen bg-background">
      {preview.enabled && <LeaderPreviewBanner />}
      <div
        data-testid="leader-layout-workspace"
        style={LEADER_VEERA_PANEL_WIDTH_STYLE}
        className={cn(
          "flex w-full min-w-0 bg-background transition-[padding-right] [transition-duration:250ms] ease-out motion-reduce:transition-none",
          livePanelOpen && LEADER_VEERA_PANEL_WIDTH_CLASS,
        )}
      >
      {/* Left Sidebar - Navigation */}
      <aside
        data-testid="leader-layout-navigation"
        className={cn(
          "sticky top-0 h-screen border-r border-sidebar-border bg-sidebar-background transition-[width] [transition-duration:250ms] ease-out motion-reduce:transition-none flex flex-col",
          effectiveCollapsed ? "w-16" : "w-64",
        )}
      >
        {/* Logo & Collapse */}
        <div className={cn(
          "flex h-16 items-center border-b border-sidebar-border",
          effectiveCollapsed ? "justify-center px-3" : "justify-between px-4",
        )}>
          {!effectiveCollapsed && <span className="text-h3 font-bold text-sidebar-foreground">{brandLabel}</span>}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            disabled={livePanelOpen}
            aria-label={livePanelOpen
              ? "Leader navigation temporarily collapsed"
              : effectiveCollapsed
                ? "Expand leader navigation"
                : "Collapse leader navigation"}
            aria-expanded={!effectiveCollapsed}
            className="shrink-0 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <ChevronLeft className={cn(
              "h-5 w-5 transition-transform motion-reduce:transition-none",
              effectiveCollapsed && "rotate-180",
            )} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-4">
          {leaderNavSections.map(section => <div key={section.title} className="space-y-1">
              {!effectiveCollapsed && <p className="px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </p>}
              {section.items.map(item => {
            const targetUrl = mapTargetUrl(item.url);
            const isActive = leaderNavTargetActive(location.pathname,location.hash,targetUrl);
            const badgeCount = getBadgeCount(item.badge);
            const badgeStyle = getBadgeStyle();
            return <div key={item.url} className="space-y-1">
                  {item.url === "/veera" && !preview.enabled ? (
                    <button
                      type="button"
                      data-tour-id={getLeaderTourId(item.url)}
                      aria-label={effectiveCollapsed ? "Coach Veera" : undefined}
                      title={effectiveCollapsed ? "Coach Veera" : undefined}
                      aria-controls={LEADER_VEERA_PANEL_ID}
                      aria-expanded={leaderVeeraPanel.open}
                      onClick={(event) => leaderVeeraPanel.openPanel(
                        { pageName: currentLeaderPage, source: "leader_navigation" },
                        event.currentTarget,
                      )}
                      className={cn(
                        "relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-small font-medium transition-colors",
                        leaderVeeraPanel.open
                          ? "bg-sidebar-accent text-sidebar-primary"
                          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      )}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!effectiveCollapsed ? <span>{item.title}</span> : null}
                    </button>
                  ) : (
                  <NavLink to={targetUrl} data-tour-id={getLeaderTourId(item.url)} className={cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-small font-medium transition-colors relative", isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground")}>
                    <div className="relative">
                      <item.icon className="h-5 w-5 shrink-0" />
                      {badgeCount > 0 && <span className={cn("absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold", badgeStyle)}>
                          {badgeCount > 9 ? "9+" : badgeCount}
                        </span>}
                    </div>
                    {!effectiveCollapsed && <div className="flex-1 space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span>{item.title}</span>
                          {isActive && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                              You are here
                            </span>}
                        </div>
                        {item.description && <p className="text-[11px] font-normal text-muted-foreground">
                            {item.description}
                          </p>}
                      </div>}
                  </NavLink>
                  )}
                </div>;
          })}
            </div>)}
        </nav>

        {showCoachBridge ? (
          <div className="px-3 pb-3">
            <Button
              type="button"
              variant="ghost"
              size={effectiveCollapsed ? "icon" : "default"}
              aria-label="Find a Coach"
              title="Find a Coach"
              className={cn(
                "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                effectiveCollapsed ? "mx-auto flex h-10 w-10" : "w-full justify-start",
              )}
              onClick={() => navigateToLeaderPath("/leader/talk-with-coach")}
            >
              <Handshake className="h-4 w-4" />
              {!effectiveCollapsed && "Find a Coach"}
            </Button>
          </div>
        ) : null}

        {/* User section with dropdown */}
        <div className="border-t border-sidebar-border p-3 space-y-3">
          <TrialExperience role="leader" compact={effectiveCollapsed} isPreview={preview.enabled} />
          <ProductFeedbackToolbarActions reporterRole="leader" className={effectiveCollapsed ? "items-center" : undefined} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-small font-medium transition-colors text-sidebar-foreground hover:bg-sidebar-accent">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                {!effectiveCollapsed && <div className="flex-1 truncate text-left">
                    <p className="text-caption font-medium text-sidebar-foreground truncate">
                      {userLabel}
                    </p>
                  </div>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigateToLeaderPath("/settings")}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              {showCoachBridge && <DropdownMenuItem onClick={() => navigateToLeaderPath("/leader/talk-with-coach")}>
                <Handshake className="mr-2 h-4 w-4" />
                Find a Coach
              </DropdownMenuItem>}
              <DropdownMenuItem onClick={() => navigateToLeaderPath("/help")}>
                <HelpCircle className="mr-2 h-4 w-4" />
                Help & Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => preview.enabled ? window.close() : void signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                {preview.enabled ? "Close preview" : "Sign out"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div data-testid="leader-layout-main" className="min-w-0 flex-1 flex flex-col overflow-hidden">
        {!suppressLifecycleAccessBanners ? (
          <>
            <CoachingPausedBanner />
            <CompletedAccessBanner />
            <ArchivedAccessBanner />
            <LeaderGracePeriodBanner />
          </>
        ) : null}
        
        {/* Content area - full width now */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
    </div>;
}
